import { FormHTMLAttributes } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import EnquiryTypes from 'constants/EnquiryTypes';

import Button from '../Button/Button';
import QuestionFormInput from '../QuestionFormInput/QuestionFormInput';
import QuestionFormTextarea from '../QuestionFormTextarea/QuestionFormTextarea';
import Select from '../Select/Select';

const ALL_STATES = [
  { id: 'QLD', name: 'Queensland' },
  { id: 'NSW', name: 'New South Wales' },
  { id: 'NT', name: 'Northern Territory' },
  { id: 'SA', name: 'South Australia' },
  { id: 'TAS', name: 'Tasmania' },
  { id: 'VIC', name: 'Victoria' },
  { id: 'WA', name: 'Western Australia' },
];

export interface QuestionFormData {
  name: string;
  email: string;
  phone: string;
  postcode: string;
  state: any;
  car_looking_for?: string;
  car_month_enquiry?: string;
  enquiry_type: string;
  message: string;
  marketing_opt_in: string;
}

export interface QuestionFormProps
  extends Omit<FormHTMLAttributes<HTMLFormElement>, 'onSubmit'> {
  onSubmit(value: QuestionFormData): any;
  withSpecificCarFields?: boolean;
  loading?: boolean;
  enquiryType: EnquiryTypes;
}

const QuestionForm = ({
  onSubmit,
  loading,
  withSpecificCarFields,
  enquiryType,
  ...props
}: QuestionFormProps) => {
  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
  } = useForm<QuestionFormData>();

  const ErrorBox = <p className="text-coral-80" />;

  return (
    <form {...props} onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-4">
        <QuestionFormInput
          {...register('name', { required: 'This field is required' })}
          placeholder="Your name"
        />
        <ErrorMessage errors={errors} name="name" as={ErrorBox} />
      </div>
      <div className="mb-4">
        <QuestionFormInput
          type="email"
          {...register('email', { required: 'This field is required' })}
          placeholder="Your email"
        />
        <ErrorMessage errors={errors} name="email" as={ErrorBox} />
      </div>
      <div className="mb-4">
        <QuestionFormInput
          {...register('phone', {
            required: 'This field is required',
            pattern: {
              value:
                /^(\+?\(61\)|\(\+?61\)|\+?61|\(0[1-9]\)|0[1-9])?( ?-?[0-9]){7,9}$/,
              message: 'This field must be an Australian number',
            },
          })}
          placeholder="Your phone number"
        />
        <ErrorMessage errors={errors} name="phone" as={ErrorBox} />
      </div>
      <div className="mb-4">
        <QuestionFormInput
          {...register('postcode', {
            required: 'This field is required',
            pattern: {
              value: /^[0-9]{4}$/,
              message: 'This field must be an Australian postcode',
            },
          })}
          placeholder="Your postcode"
        />

        <ErrorMessage errors={errors} name="postcode" as={ErrorBox} />
      </div>
      <div className="mb-4">
        <Controller
          control={control}
          name="state"
          rules={{ required: 'This field is required' }}
          render={({ field }) => (
            <Select
              options={ALL_STATES}
              placeholder="Select your state"
              classNamePrefix="react-select"
              {...field}
            />
          )}
        />
        <ErrorMessage errors={errors} name="state" as={ErrorBox} />
      </div>
      {withSpecificCarFields && (
        <>
          <div className="mb-4">
            <QuestionFormInput
              {...register('car_looking_for', {
                required: 'This field is required',
              })}
              placeholder="What car are you looking for?
              "
            />

            <ErrorMessage
              errors={errors}
              name="car_looking_for"
              as={ErrorBox}
            />
          </div>
          <div className="mb-4">
            <QuestionFormInput
              {...register('car_month_enquiry', {
                required: 'This field is required',
              })}
              placeholder="What month do you require this car?
              "
            />

            <ErrorMessage
              errors={errors}
              name="car_month_enquiry"
              as={ErrorBox}
            />
          </div>
        </>
      )}
      <div>
        <QuestionFormInput
          type="hidden"
          value={enquiryType}
          {...register('enquiry_type')}
        />
      </div>
      <div className="mb-4">
        <QuestionFormTextarea
          {...register('message', { required: 'This field is required' })}
          placeholder="Your message"
        />
        <ErrorMessage errors={errors} name="message" as={ErrorBox} />
      </div>
      <label className="flex items-center justify-center mb-4 md:justify-start">
        <input
          className="mr-3"
          type="checkbox"
          {...register('marketing_opt_in')}
        />
        <span>I would like to be kept in the Simplr. loop</span>
      </label>
      <div className="flex items-center justify-center md:justify-between">
        <Button type="submit" color="primary" disabled={loading}>
          SUBMIT
        </Button>
      </div>
    </form>
  );
};

export default QuestionForm;
